import { makeVar, useReactiveVar } from '@apollo/client';
import {
  CheckCircleIcon,
  ExclamationCircleIcon,
  ExclamationTriangleIcon,
  InformationCircleIcon,
  XMarkIcon,
} from '@heroicons/react/24/solid';
import React from 'react';
import { Intent } from '../components/ui/intent';
import { classNames } from '../utils/class-names';

// Imperative toast API with the same surface the Blueprint Toaster
// provided (AppToaster.show), rendered bottom-center by <AppToasterHost/>.

const TOAST_TIMEOUT_MS = 5000;

interface ToastOptions {
  intent?: Intent;
  /** Kept for call-site compatibility; the intent decides the icon. */
  icon?: string;
  message: React.ReactNode;
}

interface ActiveToast extends ToastOptions {
  id: number;
}

const toastsVar = makeVar<ActiveToast[]>([]);
let nextToastId = 1;

const dismissToast = (id: number) => {
  toastsVar(toastsVar().filter(t => t.id !== id));
};

export const AppToaster = {
  show(options: ToastOptions) {
    const toast: ActiveToast = { ...options, id: nextToastId++ };
    toastsVar([...toastsVar(), toast]);
    window.setTimeout(() => dismissToast(toast.id), TOAST_TIMEOUT_MS);
  },
};

const intentStyles = (intent?: Intent) => {
  switch (intent) {
    case Intent.SUCCESS:
      return { bar: 'bg-green-600', Icon: CheckCircleIcon };
    case Intent.WARNING:
      return { bar: 'bg-yellow-500', Icon: ExclamationTriangleIcon };
    case Intent.DANGER:
      return { bar: 'bg-red-600', Icon: ExclamationCircleIcon };
    case Intent.PRIMARY:
      return { bar: 'bg-primary', Icon: InformationCircleIcon };
    default:
      return { bar: 'bg-gray-700', Icon: InformationCircleIcon };
  }
};

export function AppToasterHost() {
  const toasts = useReactiveVar(toastsVar);

  if (toasts.length === 0) {
    return null;
  }

  return (
    <div
      aria-live="assertive"
      className="fixed inset-x-0 bottom-4 z-50 flex flex-col items-center gap-2 pointer-events-none"
    >
      {toasts.map(toast => {
        const { bar, Icon } = intentStyles(toast.intent);
        return (
          <div
            key={toast.id}
            className={classNames(
              'pointer-events-auto flex items-center gap-3 rounded-md px-4 py-3 text-sm font-medium text-white shadow-lg',
              bar
            )}
          >
            <Icon className="h-5 w-5 shrink-0" aria-hidden="true" />
            <span>{toast.message}</span>
            <button
              type="button"
              aria-label="Dismiss"
              className="text-white hover:text-gray-200 focus:outline-hidden"
              onClick={() => dismissToast(toast.id)}
            >
              <XMarkIcon className="h-4 w-4" aria-hidden="true" />
            </button>
          </div>
        );
      })}
    </div>
  );
}
